import React from 'react';
import { 
  Wrench, 
  ClipboardCheck, 
  Settings, 
  Users, 
  Truck, 
  Radio, 
  GraduationCap, 
  Building, 
  Cog, 
  Briefcase,
  User,
  Search,
  Handshake,
  Radar
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { JobCategory, JobWizardType } from '@/types/jobWizard';
import helicopterSilhouetteIcon from '@/assets/helicopter-silhouette-icon.png';

interface JobIconProps {
  category?: JobCategory;
  listingType?: JobWizardType;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-10 h-10',
  xl: 'w-16 h-16 md:w-24 md:h-24',
};

// Helicopter icon component styled like Lucide icons
const HelicopterIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img 
    src={helicopterSilhouetteIcon} 
    alt="" 
    className={cn(
      "inline-block object-contain",
      "transform scale-[2.2] origin-center",
      "brightness-0 saturate-100 dark:invert",
      className
    )}
  />
);

// Category icons - helicopter for pilot, Radar for drones/UAS
const categoryIcons: Record<JobCategory, React.ComponentType<{ className?: string }>> = {
  pilot: HelicopterIcon,
  mechanic: Wrench,
  camo: ClipboardCheck,
  part21: Settings,
  cabin_crew: Users,
  ground_ops: Truck,
  atc_dispatch: Radio,
  training: GraduationCap,
  drones_uas: Radar,
  airport_ops: Building,
  engineering: Cog,
  management: Briefcase,
};

// Listing type icons
const listingTypeIcons: Record<JobWizardType, React.ComponentType<{ className?: string }>> = {
  job_post: Briefcase,
  job_contract: Handshake,
  job_profile: User,
  job_seeking: Search,
};

export const JobIcon: React.FC<JobIconProps> = ({ 
  category, 
  listingType,
  size = 'md', 
  className 
}) => {
  // Determine which icon to use
  let IconComponent: React.ComponentType<{ className?: string }>;
  
  if (category) {
    IconComponent = categoryIcons[category] || Briefcase;
  } else if (listingType) {
    IconComponent = listingTypeIcons[listingType] || Briefcase;
  } else {
    IconComponent = Briefcase;
  }

  return (
    <IconComponent className={cn(sizeClasses[size], className)} />
  );
};

// Category info with colors
export const JOB_CATEGORY_COLORS: Record<JobCategory, { bg: string; text: string; border: string }> = {
  pilot: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-700' },
  mechanic: { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-700' },
  camo: { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-700' },
  part21: { bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-700' },
  cabin_crew: { bg: 'bg-pink-50 dark:bg-pink-900/30', text: 'text-pink-700 dark:text-pink-400', border: 'border-pink-200 dark:border-pink-700' },
  ground_ops: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-700' },
  atc_dispatch: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-700' },
  training: { bg: 'bg-indigo-50 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-700' },
  drones_uas: { bg: 'bg-cyan-50 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-400', border: 'border-cyan-200 dark:border-cyan-700' },
  airport_ops: { bg: 'bg-slate-50 dark:bg-slate-900/30', text: 'text-slate-700 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700' },
  engineering: { bg: 'bg-teal-50 dark:bg-teal-900/30', text: 'text-teal-700 dark:text-teal-400', border: 'border-teal-200 dark:border-teal-700' },
  management: { bg: 'bg-violet-50 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-400', border: 'border-violet-200 dark:border-violet-700' },
};

export default JobIcon;
